import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '../src/styles/color_theme.css'
import '../src/styles/custom.css'
import '../src/styles/custom.scss'
import '../src/styles/demo-6.css'
import '../src/styles/demo-7.css'
import '../src/styles/skin_color.css'
import '../src/styles/style_rtl.css'
import '../src/styles/style.css'
import '../src/styles/vendors_css.css'
import App from './App';
/* After App (antd) bundle order: keep Input.Search clear visible when text exists */
import './styles/antd-search-clear-overrides.css';
import reportWebVitals from './reportWebVitals';
import { store } from "./store";
import { Provider } from "react-redux";
import { setupCrossTabLogoutListener } from "./utils/crossTabAuthSync";

/**
 * sessionStorage is per-tab in the browser.
 * User request: "session se kro" meaning "keep session data while opening a new tab".
 *
 * We simulate shared session across tabs by:
 *  - When a tab starts, it asks other open tabs for a snapshot of known sessionStorage keys.
 *  - Other tabs respond with their snapshot.
 *  - The new tab applies those keys to its own sessionStorage before React renders.
 */
async function syncSessionStorageFromOtherTab() {
  try {
    if (typeof window === "undefined") return;
    if (typeof BroadcastChannel === "undefined") return;
    if (!window.sessionStorage) return;

    const CHANNEL_NAME = "drug-sync:session-storage-sync";
    const SYNC_KEYS = [
      "token",
      "userData",
      "familyMembers",
      "roles",
      "activeProfileContext",
      "pendingRegisterToast",
    ];

    // Create a stable tab identifier for this page load.
    const tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const channel = new BroadcastChannel(CHANNEL_NAME);

    const readSnapshot = () => {
      const snap = {};
      for (const key of SYNC_KEYS) {
        const v = window.sessionStorage.getItem(key);
        if (v !== null) snap[key] = v;
      }
      return snap;
    };

    const applySnapshot = (snapshot) => {
      if (!snapshot || typeof snapshot !== "object") return;
      for (const key of SYNC_KEYS) {
        if (Object.prototype.hasOwnProperty.call(snapshot, key)) {
          window.sessionStorage.setItem(key, String(snapshot[key]));
        } else {
          window.sessionStorage.removeItem(key);
        }
      }
    };

    // Respond to other tabs asking for a session snapshot.
    const onMessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.tabId === tabId) return;

      if (msg.type === "REQUEST_SESSION_SNAPSHOT") {
        channel.postMessage({
          type: "SESSION_SNAPSHOT",
          tabId,
          snapshot: readSnapshot(),
        });
      }
    };

    channel.addEventListener("message", onMessage);

    // Ask other tabs for their current session snapshot.
    const snapshot = await new Promise((resolve) => {
      let resolved = false;

      const MAX_WAIT_MS = 2000;
      const startAt = Date.now();

      const handleSnapshot = (event) => {
        const msg = event?.data;
        if (!msg || msg.type !== "SESSION_SNAPSHOT") return;
        if (msg.tabId === tabId) return;

        if (resolved) return;
        resolved = true;
        channel.removeEventListener("message", handleSnapshot);
        resolve(msg.snapshot || null);
      };

      channel.addEventListener("message", handleSnapshot);

      const sendRequest = () => {
        if (resolved) return;
        channel.postMessage({ type: "REQUEST_SESSION_SNAPSHOT", tabId });
      };

      // Send immediately + periodic retries until snapshot arrives.
      sendRequest();

      const interval = window.setInterval(() => {
        if (resolved) {
          window.clearInterval(interval);
          return;
        }
        if (Date.now() - startAt > MAX_WAIT_MS) {
          window.clearInterval(interval);
          return;
        }
        sendRequest();
      }, 250);

      const timeout = window.setTimeout(() => {
        if (resolved) return;
        resolved = true;
        window.clearInterval(interval);
        channel.removeEventListener("message", handleSnapshot);
        resolve(null);
      }, MAX_WAIT_MS);

      // Best-effort cleanup
      void timeout;
    });

    applySnapshot(snapshot);

    // Keep the channel open (so other future tabs can still sync).
    // No close() here.
  } catch {
    // Never block app render due to sync issues.
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));

(async () => {
  // Make sure sessionStorage is in sync before auth route guards run.
  await syncSessionStorageFromOtherTab();

  // Ensure logout in one tab logs out all tabs.
  setupCrossTabLogoutListener(store.dispatch);

  root.render(
    <Provider store={store}>
      <App />
    </Provider>
  );
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
